import {
  EncounterClass,
  EncounterService,
  type ObjectType,
} from '@quromedical/fhir-common';
import type { EncounterStatusHistory, Period } from 'fhir/r4';
import * as yup from 'yup';
import { createMediaDataSchema } from '../media/validation';
import { PregnancySchema } from '../observation/validation';
import { generalPractitionerUpdateRequestSchema } from '../patient/validation';

import type {
  AdmissionCreateMedicalAidAuthorization,
  AdmissionCreateRequest,
  AdmissionDiagnoses,
  AdmissionLocationUpdateRequest,
  AdmissionUpdateRequest,
  BaseAdmission,
} from './schema';

export const medicalAidAuthorizationCreateSchemaFields: Record<
  keyof AdmissionCreateMedicalAidAuthorization,
  yup.AnySchema
> = {
  code: yup.string().optional(),
  date: yup.date().optional(),
};

const medicalAidAuthorizationCreateSchema = yup
  .object<ObjectType<AdmissionCreateMedicalAidAuthorization>>(
    medicalAidAuthorizationCreateSchemaFields,
  )
  // yup defaults objects to an empty object, so in order for the `optional` to work we need to
  // provide a custom default
  .optional()
  .default(undefined);

const period = yup.object<ObjectType<Period>>({
  start: yup.date().required(),
  end: yup.date().optional(),
});
const statusHistorySchema = yup.object<ObjectType<EncounterStatusHistory>>({
  status: yup.string().required(),
  period: period,
});

const diagnosisSchema = yup.object<ObjectType<AdmissionDiagnoses>>({
  condition: yup.string().required(),
  type: yup.string().optional(),
});

export const allergySchema = yup.object({
  name: yup.string(),
  code: yup.string(),
});
type AdmissionRequestFields = AdmissionCreateRequest &
  Pick<BaseAdmission, 'statusHistory'>;

/**
 * Individual schema fields so that the form parts can use them individually
 */
export const admissionRequestSchemaFields: Record<
  keyof AdmissionRequestFields,
  yup.AnySchema
> = {
  periodStart: yup.date().required(),
  class: yup.string().oneOf(Object.values(EncounterClass)).required(),
  service: yup.string().oneOf(Object.values(EncounterService)).required(),
  practitioners: generalPractitionerUpdateRequestSchema.required(),
  primaryIcd10Code: yup.string().optional(),
  icd10Codes: yup.array(yup.string().required()).optional(),
  medicalAidAuthorization: medicalAidAuthorizationCreateSchema,
  oxygen: yup.number().positive().optional(),
  attachments: yup.array(createMediaDataSchema).optional(),
  statusHistory: yup.array(statusHistorySchema).optional(),
  diagnosis: yup.array(diagnosisSchema).optional(),
  pregnancy: PregnancySchema.optional(),
  acute: yup.array(yup.string().required()).optional(),
  chronic: yup.array(yup.string().required()).optional(),
  reasonOfAdmission: yup.array(yup.string().required()).min(1).required(),
  location: yup.string().required(),
  allergy: yup.array(allergySchema).optional(),
};

export const admissionCreateRequestSchema = yup
  .object<ObjectType<AdmissionCreateRequest>>(admissionRequestSchemaFields)
  .required();

export const admissionUpdateRequestSchema = yup.object<
  ObjectType<AdmissionUpdateRequest>
>({
  primaryIcd10Code: admissionRequestSchemaFields.primaryIcd10Code,
  icd10Codes: admissionRequestSchemaFields.icd10Codes,
  medicalAidAuthorization: admissionRequestSchemaFields.medicalAidAuthorization,
  oxygen: admissionRequestSchemaFields.oxygen,
  periodStart: admissionRequestSchemaFields.periodStart,
  reasonOfAdmission: admissionRequestSchemaFields.reasonOfAdmission,
});

export const admissionLocationUpdateRequestSchema = yup
  .object<ObjectType<AdmissionLocationUpdateRequest>>({
    location: yup.string().required(),
  })
  .required();
